import React, { useCallback, useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import MobilePageBar, { NewPostButton } from '../components/MobilePageBar';
import './PostsLive.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ArchivedPost {
  id: string;
  title: string | null;
  platform: string;
  status: string;
  projectName: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  postUrl: string | null;
}

interface ArchivedPostsResponse {
  count: number;
  posts: ArchivedPost[];
}

function formatPlatform(platform: string): string {
  if (platform === 'twitter') return 'X';
  if (platform === 'youtube') return 'YouTube';
  if (platform === 'tiktok') return 'TikTok';
  if (platform === 'facebook') return 'Facebook';
  if (platform === 'linkedin') return 'LinkedIn';
  if (platform === 'instagram') return 'Instagram';
  return platform;
}

function formatStatus(status: string): string {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
}

function formatDate(value: string | null): string {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleString();
}

const PostsArchived: React.FC = () => {
  const [posts, setPosts] = useState<ArchivedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadArchivedPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/posts/archived`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload.error || 'Failed to load archived posts');
      }

      const payload = await response.json() as ArchivedPostsResponse;
      setPosts(Array.isArray(payload.posts) ? payload.posts : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load archived posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadArchivedPosts();
  }, [loadArchivedPosts]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <MobilePageBar current="Archived" label="📦 Archived">
          <NewPostButton />
        </MobilePageBar>

        <div className="page-header posts-list-header">
          <div>
            <h1>Archived Posts</h1>
            <p className="page-subtitle">Posts with failed or cancelled publishing status</p>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button className="posts-live-retry-btn" onClick={() => void loadArchivedPosts()}>
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <div className="posts-live-empty-state">
            <div className="posts-live-empty-icon">⏳</div>
            <p>Loading archived posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="posts-live-empty-state">
            <div className="posts-live-empty-icon">📦</div>
            <p>No archived posts found.</p>
          </div>
        ) : (
          <div className="posts-live-list">
            {posts.map((post, index) => (
              <article key={post.id} className="posts-live-item">
                <div className="posts-live-post-number">#{index + 1}</div>
                <div className="posts-live-post-main">
                  <div className="posts-live-post-top">
                    <h3 className="posts-live-post-title">{post.title || `${formatPlatform(post.platform)} post`}</h3>
                    <div className="posts-live-badges">
                      <span className="posts-live-status-pill posts-live-status-pill--failed">{formatStatus(post.status)}</span>
                      <span className="posts-live-platform-pill">{formatPlatform(post.platform)}</span>
                    </div>
                  </div>
                  <div className="posts-live-post-meta">
                    <span>{post.projectName || 'No Project'}</span>
                    <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                  </div>
                </div>
                {post.postUrl && (
                  <div className="posts-live-item-actions">
                    <a className="posts-live-link" href={post.postUrl} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PostsArchived;
